'use client';

import DiscountCountdown from '@/app/(public)/pricing/discount-countdown';
import {
  applyDiscountRaw,
  getEffectiveDiscount,
  VALIDITY_PERIOD_LABEL,
} from '@/app/(public)/pricing/plan-utils';
import { Plan, PlanSku } from '@/app/actions/cart';
import OrderConfirmModal from '@/components/order-confirm-modal';
import { Button } from '@/components/ui/button';
import { useDisclosure } from '@/hooks/use-disclosure';
import { formatPrice } from '@/lib/util';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import { CheckIcon, CrownIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';

interface PointsPlansProps {
  plans: Plan[];
  categoryType: 'POINT_PACKAGE' | 'API_POINT_PACKAGE';
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1 },
};

const PointsPlans = ({ plans }: PointsPlansProps) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { user, triggerLogin, updateUserInfo } = useAuth();

  const [modalProps, setModalProps] = useState<{
    skuId: string;
    productName: string;
    skuName: string;
    price: number;
  } | null>(null);

  const openOrderModal = (plan: Plan, sku: PlanSku) => {
    const discount = getEffectiveDiscount(plan, sku);
    const price = applyDiscountRaw(sku.sale_price, discount);
    setModalProps({
      skuId: sku.sku_id,
      productName: plan.name,
      skuName: sku.name ?? VALIDITY_PERIOD_LABEL[sku.validity_period] ?? '',
      price,
    });
    onOpen();
  };

  const handleBuy = (plan: Plan, sku: PlanSku) => {
    if (!user && triggerLogin) {
      triggerLogin(() => openOrderModal(plan, sku));
    } else {
      openOrderModal(plan, sku);
    }
  };

  if (plans.length === 0) {
    return null;
  }

  const allSkus = plans.flatMap((plan) =>
    plan.skus.map((sku) => ({ plan, sku })),
  );

  return (
    <>
      <motion.div
        className={cn(
          'grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 mb-12',
          {
            'lg:grid-cols-3': allSkus.length === 3,
            'lg:grid-cols-4': allSkus.length >= 4,
          },
        )}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {allSkus.map(({ plan, sku }, index) => {
          const discount = getEffectiveDiscount(plan, sku);
          const rawPrice = sku.sale_price;
          const price = applyDiscountRaw(rawPrice, discount);

          const isRecommended = sku.recommended ?? false;
          const hasDiscount = discount != null;
          const isHighlighted = isRecommended;

          return (
            <motion.div
              key={sku.sku_id}
              variants={cardVariants}
              className="relative"
            >
              {/* 推荐标签 */}
              {isHighlighted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    delay: 0.4 + index * 0.1,
                    duration: 0.4,
                  }}
                  className="absolute -top-4 left-1/2 -translate-x-1/2 z-10"
                >
                  <div
                    className={cn(
                      'px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-lg text-white',
                      'bg-linear-to-r from-primary to-primary text-primary-foreground',
                    )}
                  >
                    <CrownIcon className="w-3.5 h-3.5" />
                    推荐
                  </div>
                </motion.div>
              )}

              <div
                className={cn(
                  'relative h-full p-8 rounded-xl border transition-all duration-300 hover:shadow-md flex flex-col',
                  isRecommended
                    ? 'border-2 border-primary bg-linear-to-br from-primary/5 to-primary/10 shadow-lg'
                    : hasDiscount
                      ? 'border-2 border-orange-200/80 bg-linear-to-b from-card to-orange-50/40 dark:border-orange-900/40 dark:to-orange-950/10'
                      : 'border-border bg-card',
                )}
              >
                {/* 方案头部 */}
                <div className="text-center space-y-4 mb-8">
                  <h3 className="text-2xl font-bold text-foreground">
                    {sku.name}
                  </h3>

                  {sku.point_amount != null && (
                    <div className="inline-flex items-center rounded-full border bg-secondary px-3 py-1 text-sm">
                      {sku.point_amount.toLocaleString()} 积分 · 永久有效
                    </div>
                  )}

                  {/* 价格 */}
                  <div className="mt-4 space-y-1.5">
                    {hasDiscount && discount && (
                      <div className="inline-flex rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 dark:border-orange-900/60 dark:bg-orange-950/30 dark:text-orange-300">
                        {discount.name}
                      </div>
                    )}
                    {rawPrice !== price && rawPrice > 0 && (
                      <div className="text-sm text-muted-foreground line-through h-5">
                        {formatPrice(rawPrice)}
                      </div>
                    )}
                    <div className="text-3xl lg:text-4xl font-bold text-foreground">
                      {formatPrice(price)}
                    </div>
                    {sku.point_amount != null && sku.point_amount > 0 && (
                      <div className="text-sm text-muted-foreground">
                        约{' '}
                        {formatPrice(
                          Math.round((price / sku.point_amount) * 1000),
                        )}
                        /千积分
                      </div>
                    )}
                    {hasDiscount && rawPrice > price && (
                      <div className="text-xs font-medium flex items-center justify-center gap-1.5 flex-wrap">
                        <DiscountCountdown endTime={discount!.end_time} />
                      </div>
                    )}
                  </div>
                </div>

                {/* CTA 按钮 */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="mb-8"
                >
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => handleBuy(plan, sku)}
                  >
                    立即购买
                  </Button>
                </motion.div>

                {/* 功能列表 */}
                <div className="space-y-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground border-b pb-2">
                    包含内容
                  </h4>
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.08 }}
                    className="flex items-center gap-3"
                  >
                    <div className="shrink-0 w-5 h-5 flex items-center justify-center">
                      <CheckIcon className="w-3.5 h-3.5 text-foreground" />
                    </div>
                    <span className="text-sm text-secondary-foreground">
                      {sku.point_amount?.toLocaleString()} 积分
                    </span>
                  </motion.div>

                  {plan.features?.map((feature, k) => (
                    <motion.div
                      key={k}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.5 + index * 0.08 + (k + 1) * 0.04,
                      }}
                      className="flex items-center gap-3"
                    >
                      <div className="shrink-0 w-5 h-5 flex items-center justify-center">
                        <CheckIcon className="w-3.5 h-3.5 text-foreground" />
                      </div>
                      <span className="text-sm text-secondary-foreground">
                        {feature}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {modalProps && (
        <OrderConfirmModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          skuId={modalProps.skuId}
          productName={modalProps.productName}
          skuName={modalProps.skuName}
          price={modalProps.price}
          onSuccess={() => updateUserInfo()}
        />
      )}
    </>
  );
};

export default PointsPlans;
